import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Box, 
  Button, 
  Grid, 
  Paper, 
  Divider, 
  CircularProgress,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Flag as FlagIcon,
  Place as PlaceIcon,
  SportsScore as SportsScoreIcon,
  Person as PersonIcon,
  Straighten as StraightenIcon,
  Event as EventIcon,
  EventAvailable as EventAvailableIcon,
  CalendarToday as CalendarTodayIcon,
  DirectionsCar as DirectionsCarIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { racetrack as racetrackApi } from '../../utils/api';
import { useAuth } from '../../components/auth/AuthModule';
import { COUNTRIES } from '../../constants/countries';
import { COUNTRY_TO_CODE } from '../../utils/countryUtils';
import ReactCountryFlag from 'react-country-flag';
import { format } from 'date-fns';

const RacetrackDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(['racetracks', 'common', 'events']);
  const { user } = useAuth();
  
  const [racetrack, setRacetrack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editedRacetrack, setEditedRacetrack] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);

  useEffect(() => {
    if (id) {
      fetchRacetrackDetails();
    }
  }, [id]);

  const fetchRacetrackDetails = async () => {
    try {
      setLoading(true);
      const data = await racetrackApi.getById(id);
      setRacetrack(data);
      setEditedRacetrack({
        name: data.name || '',
        length: data.length || '',
        country: data.country || '',
        location: data.location || ''
      });

      // Process events if they exist
      if (data.events && data.events.length > 0) {
        const now = new Date();
        const upcoming = [];
        const past = [];

        data.events.forEach(event => {
          const eventDate = new Date(event.date);
          if (eventDate >= now) {
            upcoming.push(event);
          } else {
            past.push(event);
          }
        });

        // Sort upcoming events by date (ascending)
        upcoming.sort((a, b) => new Date(a.date) - new Date(b.date));
        // Sort past events by date (descending)
        past.sort((a, b) => new Date(b.date) - new Date(a.date));

        setUpcomingEvents(upcoming);
        setPastEvents(past);
      }
    } catch (error) {
      console.error('Error fetching racetrack details:', error);
      toast.error(t('errors.loadingRacetrackDetails'));
    } finally {
      setLoading(false);
    }
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
    if (!isEditing) {
      setEditedRacetrack({
        name: racetrack.name || '',
        length: racetrack.length || '',
        country: racetrack.country || '',
        location: racetrack.location || ''
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedRacetrack(prev => ({
      ...prev,
      [name]: name === 'length' ? parseFloat(value) || 0 : value
    }));
  };

  const handleSaveChanges = async () => {
    try {
      await racetrackApi.update(id, editedRacetrack);
      toast.success(t('success.racetrackUpdated'));
      setIsEditing(false);
      fetchRacetrackDetails();
    } catch (error) {
      console.error('Error updating racetrack:', error);
      toast.error(t('errors.updatingRacetrack'));
    }
  };

  const handleDeleteRacetrack = async () => {
    try {
      await racetrackApi.delete(id);
      toast.success(t('success.racetrackDeleted'));
      navigate('/racetracks');
    } catch (error) {
      console.error('Error deleting racetrack:', error);
      toast.error(t('errors.deletingRacetrack'));
    }
  };

  const isCreator = () => {
    return user && racetrack && racetrack.createdBy && 
           (user._id === racetrack.createdBy._id || user.isAdmin);
  };

  const navigateToEvent = (eventId) => {
    navigate(`/events/${eventId}`);
  };

  const renderEventsList = (events, title, emptyMessage, icon) => {
    return (
      <Box mt={3}>
        <Box display="flex" alignItems="center" mb={2}>
          {icon}
          <Typography variant="h6" component="h3">
            {title}
          </Typography>
        </Box>
        
        {events.length > 0 ? (
          <List>
            {events.map(event => (
              <Card key={event._id} sx={{ mb: 2 }}>
                <CardActionArea onClick={() => navigateToEvent(event._id)}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="h6" component="div">
                          {event.title}
                        </Typography>
                        <Box display="flex" alignItems="center" mt={1}>
                          <CalendarTodayIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {format(new Date(event.date), 'PPP')}
                            {event.endDate && ` - ${format(new Date(event.endDate), 'PPP')}`}
                          </Typography>
                        </Box>
                      </Box>
                      <Chip 
                        icon={<DirectionsCarIcon />} 
                        label="Race Day" 
                        color="secondary" 
                        size="small" 
                      />
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {emptyMessage}
          </Typography>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!racetrack) {
    return (
      <Box p={3}>
        <Typography>{t('racetrackDetail.notFound')}</Typography>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/racetracks')}
          sx={{ mt: 2 }}
        >
          {t('common:back')}
        </Button>
      </Box>
    );
  }

  const countryCode = COUNTRY_TO_CODE[racetrack.country];

  return (
    <Container maxWidth="lg">
      <Box mt={4}>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/racetracks')}
          sx={{ mb: 2 }}
        >
          {t('common:back')}
        </Button>
        
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            {isEditing ? t('racetrackDetail.editTitle') : t('racetrackDetail.title')}
          </Typography>
          
          {isCreator() && (
            <Box>
              {isEditing ? (
                <>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleEditToggle}
                    sx={{ mr: 1 }}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveChanges}
                  >
                    {t('common.save')}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<EditIcon />}
                    onClick={handleEditToggle}
                    sx={{ mr: 1 }}
                  >
                    {t('common.edit')}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteDialogOpen(true)}
                  >
                    {t('common.delete')}
                  </Button>
                </>
              )}
            </Box>
          )}
        </Box>
        
        <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
          {isEditing ? (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('racetrackDetail.name')}
                  name="name"
                  value={editedRacetrack.name}
                  onChange={handleInputChange}
                  margin="normal"
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('racetrackDetail.length')}
                  name="length"
                  type="number"
                  value={editedRacetrack.length}
                  onChange={handleInputChange}
                  margin="normal"
                  variant="outlined"
                  InputProps={{
                    endAdornment: t('racetrackDetail.kilometers')
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>{t('racetrackDetail.country')}</InputLabel>
                  <Select
                    name="country"
                    value={editedRacetrack.country}
                    onChange={handleInputChange}
                    label={t('racetrackDetail.country')}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {COUNTRY_TO_CODE[selected] && (
                          <ReactCountryFlag 
                            countryCode={COUNTRY_TO_CODE[selected]} 
                            svg 
                            style={{ 
                              width: '1.5em', 
                              height: '1.5em',
                              marginRight: '8px',
                              borderRadius: '2px'
                            }}
                          />
                        )}
                        {selected}
                      </Box>
                    )}
                  >
                    {COUNTRIES.map((country) => (
                      <MenuItem key={country} value={country}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {COUNTRY_TO_CODE[country] && (
                            <ReactCountryFlag 
                              countryCode={COUNTRY_TO_CODE[country]} 
                              svg 
                              style={{ 
                                width: '1.5em', 
                                height: '1.5em',
                                marginRight: '8px',
                                borderRadius: '2px'
                              }}
                            />
                          )}
                          {country}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('racetrackDetail.location')}
                  name="location"
                  value={editedRacetrack.location}
                  onChange={handleInputChange}
                  margin="normal"
                  variant="outlined"
                />
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" mb={2}>
                  <SportsScoreIcon color="primary" sx={{ mr: 2, fontSize: 30 }} />
                  <Typography variant="h5" component="h2">
                    {racetrack.name}
                  </Typography>
                </Box>
                <Divider sx={{ my: 2 }} />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center" mb={2}>
                  <StraightenIcon sx={{ mr: 2, color: 'text.secondary' }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {t('racetrackDetail.length')}
                    </Typography>
                    <Typography variant="body1">
                      {racetrack.length} {t('racetrackDetail.kilometers')}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center" mb={2}>
                  {countryCode ? (
                    <ReactCountryFlag 
                      countryCode={countryCode} 
                      svg 
                      style={{ 
                        width: '2em', 
                        height: '2em',
                        marginRight: '16px',
                        borderRadius: '2px'
                      }}
                      title={racetrack.country}
                    />
                  ) : (
                    <FlagIcon sx={{ mr: 2, color: 'text.secondary' }} />
                  )}
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {t('racetrackDetail.country')}
                    </Typography>
                    <Typography variant="body1">
                      {racetrack.country}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center" mb={2}>
                  <PlaceIcon sx={{ mr: 2, color: 'text.secondary' }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {t('racetrackDetail.location')}
                    </Typography>
                    <Typography variant="body1">
                      {racetrack.location}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center" mb={2}>
                  <PersonIcon sx={{ mr: 2, color: 'text.secondary' }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {t('racetrackDetail.createdBy')}
                    </Typography>
                    <Typography variant="body1">
                      {racetrack.createdBy?.name || racetrack.createdBy?.username || t('common.unknown')}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          )}
        </Paper>
        
        {/* Events Section */}
        {!isEditing && (
          <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              {t('racetrackDetail.events')}
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            {renderEventsList(
              upcomingEvents, 
              t('events:events.upcomingEvents'), 
              t('racetrackDetail.noUpcomingEvents'),
              <EventAvailableIcon color="primary" sx={{ mr: 2, fontSize: 24 }} />
            )}
            
            {renderEventsList(
              pastEvents, 
              t('events:events.pastEvents'), 
              t('racetrackDetail.noPastEvents'),
              <EventIcon color="primary" sx={{ mr: 2, fontSize: 24 }} />
            )}
            
            {upcomingEvents.length === 0 && pastEvents.length === 0 && (
              <Box textAlign="center" py={4}>
                <EventIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  {t('racetrackDetail.noEvents')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('racetrackDetail.createEventPrompt')}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate('/events')}
                  sx={{ mt: 2 }}
                >
                  {t('racetrackDetail.goToEvents')}
                </Button>
              </Box>
            )}
          </Paper>
        )}
      </Box>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('racetracks.deleteRacetrack')}</DialogTitle>
        <DialogContent>
          <Typography>{t('racetrackDetail.confirmDelete')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleDeleteRacetrack} color="error" variant="contained">{t('common.delete')}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default RacetrackDetailPage; 